import React, { useState } from "react";
import { Card, Tag, Space, Button, Tooltip, Typography } from "antd";
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  ReloadOutlined,
  PlusOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import axios from "axios";

const { Text } = Typography;

/**
 * Editable Production Stages configurator for a Job.
 * Shows applicable stages as a flow, skipped stages below.
 * Edit mode lets you toggle stages on/off.
 */
const StageConfigurator = ({ job, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applicable, setApplicable] = useState([]);
  const [skipped, setSkipped] = useState([]);

  // All stages in the correct flow order
  const ALL_STAGES = [
    "order_received",
    "pending_inventory",
    "inventory_fulfilled",
    "ready_for_production",
    "pre_print",
    "coating",
    "corrugation",
    "sheet_cutting",
    "pasting",
    "casting",
    "die_cutting",
    "joining",
    "after_box_print",
    "qc",
    "ready_for_dispatch",
    "dispatched",
  ];

  const DISPLAY_NAMES = {
    order_received: "Order Received",
    pending_inventory: "Pending Inventory",
    inventory_fulfilled: "Inventory Fulfilled",
    ready_for_production: "Ready for Production",
    pre_print: "Pre-Print",
    coating: "Coating",
    corrugation: "Corrugation",
    sheet_cutting: "Sheet Cutting",
    pasting: "Pasting",
    casting: "Casting",
    die_cutting: "Casting",
    joining: "Joining",
    after_box_print: "After Box Print",
    qc: "QC",
    ready_for_dispatch: "Ready for Dispatch",
    dispatched: "Dispatched",
  };

  // Stages that can't be removed (core logistics/tracking)
  const LOCKED_STAGES = ["order_received", "dispatched"];

  const startEdit = () => {
    setApplicable([...job.applicable_stages]);
    setSkipped([...(job.skipped_stages || [])]);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
  };

  const toggleStage = (code) => {
    if (LOCKED_STAGES.includes(code)) return;

    if (applicable.includes(code)) {
      // Move to skipped
      setApplicable((prev) => prev.filter((s) => s !== code));
      setSkipped((prev) => {
        if (prev.includes(code)) return prev;
        return [...prev, code];
      });
    } else {
      // Move to applicable — insert in correct flow order
      setSkipped((prev) => prev.filter((s) => s !== code));
      setApplicable((prev) => {
        const updated = [...prev, code];
        // Sort by ALL_STAGES order
        return updated.sort(
          (a, b) => ALL_STAGES.indexOf(a) - ALL_STAGES.indexOf(b)
        );
      });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`job/${job.id}`, {
        applicable_stages: applicable,
        skipped_stages: skipped,
      });
      // Re-run machine selection to match the new stages
      try {
        await axios.post(`job/${job.id}/auto-select-machines`);
      } catch (_) { /* non-critical */ }
      toast.success("Production stages & machines updated");
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update stages");
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      setSaving(true);
      const { data } = await axios.post(`job/${job.id}/recalculate-stages`);
      // Re-run machine selection to match the new stages
      try {
        await axios.post(`job/${job.id}/auto-select-machines`);
      } catch (_) { /* non-critical */ }
      toast.success("Stages & machines recalculated from job specs");
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Failed to recalculate stages");
    } finally {
      setSaving(false);
    }
  };

  const displayApplicable = editing ? applicable : job.applicable_stages;
  const displaySkipped = editing ? skipped : job.skipped_stages || [];

  // All known stage codes (union of applicable + skipped + ALL_STAGES that appear)
  const allKnown = ALL_STAGES.filter(
    (s) => displayApplicable.includes(s) || displaySkipped.includes(s)
  );

  const currentIdx = displayApplicable.indexOf(job.current_stage);

  return (
    <Card
      title="Production Stages"
      size="small"
      style={{ marginBottom: 16 }}
      extra={
        editing ? (
          <Space size={4}>
            <Tooltip title="Auto-detect stages from job specs">
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={handleRecalculate}
                loading={saving}
              >
                Auto
              </Button>
            </Tooltip>
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={cancelEdit}
              disabled={saving}
            />
            <Button
              size="small"
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              Save
            </Button>
          </Space>
        ) : (
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={startEdit}
          >
            Edit
          </Button>
        )
      }
    >
      {/* Active stages flow */}
      <Space size={[4, 6]} wrap>
        {displayApplicable.map((stageCode, idx) => {
          const displayCode =
            stageCode === "die_cutting" ? "casting" : stageCode;
          const displayName =
            DISPLAY_NAMES[stageCode] || displayCode.replace(/_/g, " ");
          const isCurrent = job.current_stage === stageCode;
          const isPast = idx < currentIdx;
          const isLocked = LOCKED_STAGES.includes(stageCode);

          return (
            <React.Fragment key={stageCode}>
              {idx > 0 && (
                <span style={{ color: "#d9d9d9", fontSize: 11 }}>→</span>
              )}
              <Tooltip
                title={
                  editing
                    ? isLocked
                      ? "This stage cannot be removed"
                      : "Click to skip this stage"
                    : displayName
                }
              >
                <Tag
                  color={isCurrent ? "blue" : isPast ? "green" : "default"}
                  style={{
                    fontSize: 11,
                    fontWeight: isCurrent ? 700 : 400,
                    cursor: editing && !isLocked ? "pointer" : "default",
                    opacity: editing && isLocked ? 0.6 : 1,
                  }}
                  onClick={() => editing && toggleStage(stageCode)}
                >
                  {isCurrent && "▶ "}
                  {displayName.toLowerCase()}
                  {editing && !isLocked && (
                    <MinusOutlined
                      style={{ marginLeft: 4, fontSize: 9, color: "#ff4d4f" }}
                    />
                  )}
                </Tag>
              </Tooltip>
            </React.Fragment>
          );
        })}
      </Space>

      {/* Skipped stages */}
      {(displaySkipped.length > 0 || editing) && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Skipped:{" "}
          </Text>
          {displaySkipped.length === 0 && (
            <Text type="secondary" style={{ fontSize: 10 }}>
              (none)
            </Text>
          )}
          {displaySkipped
            .sort(
              (a, b) => ALL_STAGES.indexOf(a) - ALL_STAGES.indexOf(b)
            )
            .map((s) => {
              const displayName =
                DISPLAY_NAMES[s] || s.replace(/_/g, " ");
              return (
                <Tooltip
                  key={s}
                  title={editing ? "Click to add this stage back" : displayName}
                >
                  <Tag
                    style={{
                      fontSize: 10,
                      textDecoration: editing ? "none" : "line-through",
                      opacity: editing ? 0.8 : 0.6,
                      cursor: editing ? "pointer" : "default",
                      borderStyle: editing ? "dashed" : "solid",
                    }}
                    onClick={() => editing && toggleStage(s)}
                  >
                    {displayName.toLowerCase()}
                    {editing && (
                      <PlusOutlined
                        style={{
                          marginLeft: 4,
                          fontSize: 9,
                          color: "#52c41a",
                        }}
                      />
                    )}
                  </Tag>
                </Tooltip>
              );
            })}
        </div>
      )}
    </Card>
  );
};

export default StageConfigurator;
